import React, { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Calculator, TrendingDown, DollarSign, Percent, Shield } from "lucide-react";
import AmortizationSchedule from "./AmortizationSchedule";
import { computeMI, LOAN_TYPES } from "../lib/mi";
import MoneyInput from "../portal/shared/MoneyInput";
import NumberInput from "../portal/shared/NumberInput";

const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function calcMonthly(principal, rate, years) {
  const monthlyRate = rate / 100 / 12;
  const n = years * 12;
  if (monthlyRate === 0) return principal / n;
  return (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
}

export default function MortgageCalculator() {
  const [loanType, setLoanType] = useState("conventional");
  const [price, setPrice] = useState(425000);
  const [downPct, setDownPct] = useState(10);
  const [rate, setRate] = useState(6.49);
  const [years, setYears] = useState(30);
  const [taxRate, setTaxRate] = useState(1.8);
  const [insurance, setInsurance] = useState(1500);

  const result = useMemo(() => {
    const baseLoan = price * (1 - downPct / 100);
    const mi = computeMI({ loanType, loanAmount: baseLoan, propertyPrice: price, termYears: years });
    // Upfront fees (FHA UFMIP, VA funding fee, USDA guarantee) are typically financed
    const principal = mi.upfront_financed ? baseLoan + mi.upfront_fee : baseLoan;
    const pAndI = calcMonthly(principal, rate, years);
    const tax = (price * (taxRate / 100)) / 12;
    const ins = insurance / 12;
    const miMonthly = mi.monthly;
    const total = pAndI + tax + ins + miMonthly;
    const lifetimeInterest = pAndI * years * 12 - principal;
    return { principal, baseLoan, pAndI, tax, ins, mi: miMonthly, miInfo: mi, total, lifetimeInterest };
  }, [loanType, price, downPct, rate, years, taxRate, insurance]);

  const breakdown = [
    { label: "Principal & Interest", value: result.pAndI, color: "#070B5F" },
    { label: "Property Tax", value: result.tax, color: "#1A24A0" },
    { label: "Home Insurance", value: result.ins, color: "#6BA82E" },
    ...(result.mi > 0 ? [{ label: result.miInfo.mi_type, value: result.mi, color: "#A6E56C" }] : []),
  ];
  const maxSeg = Math.max(...breakdown.map(b => b.value));

  return (
    <section id="calculator" className="section-pad relative overflow-hidden" style={{ background: "#faf7f2" }}>
      <div className="grid-bg absolute inset-0 opacity-40 pointer-events-none" />
      <div className="orb" style={{ width: 360, height: 360, background: "#6BA82E", top: -60, right: -80, opacity: 0.25 }} />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center max-w-2xl mx-auto reveal">
          <span className="gold-bar" />
          <div className="text-xs uppercase tracking-[0.28em] font-semibold mt-3" style={{ color: "#6BA82E" }}>Mortgage Calculator</div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3" style={{ color: "#070B5F" }}>
            See what you can afford — in seconds.
          </h2>
          <p className="mt-4 text-slate-600">Adjust the numbers below to see your estimated monthly mortgage payment.</p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-8 reveal">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-white rounded-3xl p-8 shadow-3d border border-slate-100">
            <Tabs defaultValue="purchase" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="purchase">Purchase</TabsTrigger>
                <TabsTrigger value="refinance">Refinance</TabsTrigger>
              </TabsList>
              <TabsContent value="purchase" className="space-y-6">
                <div>
                  <Label className="text-sm font-semibold mb-3 block" style={{ color: "#070B5F" }}>Loan Program</Label>
                  <div className="grid grid-cols-5 gap-2">
                    {LOAN_TYPES.map(t => (
                      <button
                        key={t.key}
                        onClick={() => setLoanType(t.key)}
                        data-testid={`loan-type-${t.key}`}
                        className={`py-2 rounded-lg text-xs font-semibold transition-all ${loanType === t.key ? "text-white shadow-3d" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        style={loanType === t.key ? { background: "#070B5F" } : {}}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="Home Price" icon={DollarSign} value={price} onValue={setPrice} min={50000} max={2000000} step={5000} format={fmt} editable kind="money" />
                <Field label="Down Payment %" icon={Percent} value={downPct} onValue={setDownPct} min={0} max={60} step={1} format={(v)=>`${v}%`} editable suffix="%" />
                <Field label="Interest Rate" icon={TrendingDown} value={rate} onValue={setRate} min={2} max={12} step={0.5} format={(v)=>`${v.toFixed(3)}%`} editable suffix="%" />
                <div>
                  <Label className="text-sm font-semibold mb-3 block" style={{ color: "#070B5F" }}>Loan Term</Label>
                  <div className="flex gap-2">
                    {[15, 20, 30].map(y => (
                      <button key={y} onClick={() => setYears(y)} className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${years === y ? "text-white shadow-3d" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} style={years === y ? { background: "#070B5F" } : {}}>
                        {y} years
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                  <div>
                    <Label className="text-xs text-slate-500">Property Tax (%/yr)</Label>
                    <NumberInput value={taxRate} onChange={(n) => setTaxRate(n ?? 0)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Home Insurance ($/yr)</Label>
                    <MoneyInput value={insurance} onChange={(n) => setInsurance(n ?? 0)} className="mt-1" />
                  </div>
                </div>

                {/* MI / Fee info panel */}
                <div className="pt-3 border-t border-slate-100" data-testid="public-mi-panel">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Shield size={14} style={{ color: "#6BA82E" }} />
                    <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#6BA82E" }}>
                      {result.miInfo.mi_type || "No MI"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 leading-relaxed">{result.miInfo.notes}</div>
                  {result.miInfo.upfront_fee > 0 && (
                    <div className="mt-2 flex items-center justify-between text-xs bg-slate-50 rounded-lg p-2">
                      <span className="text-slate-500">Upfront fee {result.miInfo.upfront_financed ? "(financed into loan)" : ""}</span>
                      <span className="font-serif font-bold" style={{ color: "#070B5F" }}>{fmt(result.miInfo.upfront_fee)}</span>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="refinance" className="space-y-6">
                <Field label="Current Loan Balance" icon={DollarSign} value={price} onValue={setPrice} min={50000} max={2000000} step={5000} format={fmt} editable kind="money" />
                <Field label="New Rate" icon={TrendingDown} value={rate} onValue={setRate} min={2} max={12} step={0.5} format={(v)=>`${v.toFixed(3)}%`} editable suffix="%" />
                <div>
                  <Label className="text-sm font-semibold mb-3 block" style={{ color: "#070B5F" }}>New Term</Label>
                  <div className="flex gap-2">
                    {[15, 20, 30].map(y => (
                      <button key={y} onClick={() => setYears(y)} className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${years === y ? "text-white shadow-3d" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} style={years === y ? { background: "#070B5F" } : {}}>
                        {y} years
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Result */}
          <div className="lg:col-span-2 rounded-3xl p-8 shadow-3d-lg text-white relative overflow-hidden" style={{ background: "linear-gradient(160deg, #030524 0%, #070B5F 60%, #0F1780 100%)" }}>
            <div className="orb" style={{ width: 240, height: 240, background: "#6BA82E", top: -60, right: -60, opacity: 0.4 }} />
            <div className="relative">
              <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest">
                <Calculator size={14} /> Estimated Monthly Payment
              </div>
              <div className="font-serif text-5xl md:text-6xl font-bold mt-3 stat-num text-gold-grad">
                {fmt(result.total)}
              </div>
              <div className="text-white/60 text-sm mt-2">Loan amount: {fmt(result.principal)}</div>

              <div className="mt-8 space-y-3">
                {breakdown.map(b => (
                  <div key={b.label}>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/80">{b.label}</span>
                      <span className="font-semibold">{fmt(b.value)}/mo</span>
                    </div>
                    <div className="h-1.5 mt-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(b.value / maxSeg) * 100}%`, background: b.color === "#6BA82E" ? "linear-gradient(90deg, #8DC63F, #6BA82E)" : b.color === "#070B5F" ? "#8DC63F" : "rgba(166,229,108,0.6)" }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-widest">Lifetime Interest</div>
                  <div className="font-serif text-2xl font-bold mt-1">{fmt(result.lifetimeInterest)}</div>
                </div>
                <a href="#contact" className="btn-gold">Get Pre-Approved</a>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">*Estimates only. Actual rates, taxes and insurance may vary. Contact us for a personalized quote.</p>

        <div className="mt-8 reveal">
          <AmortizationSchedule principal={result.principal} rate={rate} years={years} testId="public-amort" />
        </div>
      </div>
    </section>
  );
}

function Field({ label, icon: Icon, value, onValue, min, max, step, format, editable = false, suffix = "", kind = "number" }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-semibold flex items-center gap-2" style={{ color: "#070B5F" }}>
          <Icon size={15} style={{ color: "#6BA82E" }} /> {label}
        </Label>
        {editable ? (
          <div className="flex items-center gap-1">
            {kind === "money" ? (
              <MoneyInput
                value={value}
                onChange={(n) => onValue(n ?? 0)}
                className="w-28 text-right font-serif font-bold text-lg px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:border-[#6BA82E]"
                style={{ color: "#070B5F" }}
              />
            ) : (
              <NumberInput
                value={value}
                min={min}
                max={max}
                onChange={(n) => onValue(n ?? 0)}
                className="w-24 text-right font-serif font-bold text-lg px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:border-[#6BA82E]"
                style={{ color: "#070B5F" }}
              />
            )}
            {suffix && <span className="font-serif font-bold text-lg" style={{ color: "#070B5F" }}>{suffix}</span>}
          </div>
        ) : (
          <div className="font-serif font-bold text-lg" style={{ color: "#070B5F" }}>{format(value)}</div>
        )}
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onValue(v[0])} />
    </div>
  );
}
